import { Node, mergeAttributes } from '@tiptap/core';
import { syncIdAttributes } from '../../extensions/CustomUniqueId';
import { TextSelection } from '@tiptap/pm/state';
import getNestedInsertionGuardPlugin, {
  violatesTemplateFieldNesting,
} from '../../plugins/getNestedInsertionGuardPlugin';
import { TEMPLATE_FIELD_BLOCK_NAME, templateFieldWrapperClass } from '../../../doc-engine/templateFields';
import placeCaretBelow from '../../utils/placeCaretBelow';
import { getFormFieldExitPos, isFieldInCard } from '../formField/formFieldHelpers';
import { selectFieldOnEdgeClick } from './selectFieldOnEdgeClick';

const TemplateFieldBlockNode = Node.create({
  name: TEMPLATE_FIELD_BLOCK_NAME,

  group: 'block blockTemplateField',
  // heading intentionally excluded (v1: all headings come from template structure)
  // inline-block excluded: it's group:'inline' and belongs inside paragraphs (accessible via inline*)
  // commentSelection = the selection-node comment wrapper, so block-level comments (images, tables,
  // multi-block spans) can be anchored inside the field exactly like in a normal document.
  content:
    '(paragraph | table | image | fieldBlock | bulletList | orderedListWithStart | taskList | commentSelection)+',
  isolating: true,
  defining: true,
  atom: false,
  draggable: true,

  // `id` and `fieldId` come from the `addUniqueIdAttribute()` wrapper, not from this node.

  parseHTML() {
    return [{ tag: 'template-field-block' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['template-field-block', mergeAttributes(HTMLAttributes), 0];
  },

  addKeyboardShortcuts() {
    return {
      // Delete empty block field on Backspace. In bound docs, TemplateLocking rejects - safe no-op.
      // No position check: `isolating: true` prevents joinBackward for non-empty fields.
      Backspace: (): boolean => {
        const { state } = this.editor;
        const { $from, empty } = state.selection;
        if (!empty) return false;

        for (let d = $from.depth; d >= 0; d--) {
          if ($from.node(d).type !== this.type) continue;

          const field = $from.node(d);
          const isEmpty =
            field.childCount === 1 && field.firstChild!.isTextblock && field.firstChild!.content.size === 0;

          if (isEmpty) {
            // A card's field stays: the card goes with its Delete button or its own selection.
            if (isFieldInCard(state.doc, $from.before(d))) return true;
            this.editor.view.dispatch(state.tr.delete($from.before(d), $from.after(d)));
            return true;
          }
          return false; // non-empty: let ProseMirror handle
        }
        return false;
      },

      // Exit on double Enter: when cursor is in an empty trailing paragraph that is a
      // direct child of the block field, lift it out (same UX as lists/blockquotes).
      Enter: (): boolean => {
        const { state } = this.editor;
        const { $from, empty } = state.selection;
        if (!empty) return false;

        for (let d = $from.depth; d >= 0; d--) {
          if ($from.node(d).type !== this.type) continue;

          // Only direct children of the field (depth d+1), not deep inside lists/tables.
          if ($from.depth !== d + 1) return false;

          const field = $from.node(d);
          const para = $from.parent; // guaranteed to be field's direct child (depth check above)
          if (!para.isTextblock || para.content.size !== 0) return false;

          // Only the trailing paragraph triggers exit - mid-field empty paragraphs are legitimate content separators.
          const paraIndex = $from.index(d);
          if (paraIndex !== field.childCount - 1) return false;

          // Also require at least one sibling - don't exit if this is the only child
          // (that would leave an empty field; outside a card, Backspace deletes it instead).
          if (field.childCount < 2) return false;

          const paragraphType = state.schema.nodes.paragraph;
          if (!paragraphType) return false;

          // Delete the empty trailing paragraph and leave the card for the empty paragraph below it.
          const paraStart = $from.before(d + 1);
          const paraEnd = $from.after(d + 1);
          const cardEnd = getFormFieldExitPos($from);
          let tr = state.tr.delete(paraStart, paraEnd);
          if (cardEnd !== undefined) {
            this.editor.view.dispatch(placeCaretBelow(tr, tr.mapping.map(cardEnd)));
            return true;
          }
          // Outside a card a new paragraph: a bound document's caret would otherwise leave for locked text.
          const mappedAfter = tr.mapping.map($from.after(d));
          tr = tr.insert(mappedAfter, paragraphType.createAndFill()!);
          tr = tr.setSelection(TextSelection.create(tr.doc, mappedAfter + 1));
          this.editor.view.dispatch(tr.scrollIntoView());
          return true;
        }
        return false;
      },
    };
  },

  addProseMirrorPlugins() {
    // One guard for every field kind. The schema cannot say it: a table cell inside a block field takes
    // 'block+', which admits headings, page breaks and other fields.
    return [getNestedInsertionGuardPlugin(violatesTemplateFieldNesting, 'templateFieldNestingGuard')];
  },

  addNodeView() {
    return ({ node, editor, getPos }) => {
      const wrapper = document.createElement('div');
      wrapper.className = templateFieldWrapperClass(TEMPLATE_FIELD_BLOCK_NAME);
      syncIdAttributes(wrapper, node);
      wrapper.setAttribute('data-node-view-wrapper', '');

      const content = document.createElement('div');
      content.className = 'template-field-block-content';
      content.setAttribute('data-node-view-content', '');

      wrapper.append(content);
      const stopSelectingOnEdgeClick = selectFieldOnEdgeClick(wrapper, editor, getPos);

      return {
        dom: wrapper,
        contentDOM: content,
        update(updatedNode) {
          if (updatedNode.type !== node.type) return false;
          syncIdAttributes(wrapper, updatedNode);
          return true;
        },
        destroy: stopSelectingOnEdgeClick,
      };
    };
  },
});

export default TemplateFieldBlockNode;
